/**
 * Centre d'aide — contenu contextuel (généré + maintenu à la main).
 *
 * Une entrée par route (clé = route.name), en français simple pour des
 * utilisateurs non techniques. Consommé par HelpBubble.vue (aide de la page
 * courante) et HelpCenterView.vue (page /aide).
 *
 *   HELP_GROUPS : thèmes pour regrouper les articles sur /aide.
 *   HELP        : { [routeName]: { group, icon, title, summary, steps[], faqs[], tour?[] } }
 *
 * 'tour' (optionnel) = étapes surlignées (voir src/utils/guidedTour.js) ;
 * chaque étape : { selector, title, body }.
 */

export const HELP_GROUPS = [
  {
    "key": "dashboard",
    "label": "Tableau de bord"
  },
  {
    "key": "reports",
    "label": "Rapports"
  },
  {
    "key": "catalog",
    "label": "Catalogue"
  },
  {
    "key": "inventory",
    "label": "Inventaire"
  },
  {
    "key": "ingredients",
    "label": "Ingrédients"
  },
  {
    "key": "customers",
    "label": "Clients"
  },
  {
    "key": "restaurant",
    "label": "Restaurant"
  },
  {
    "key": "natcash",
    "label": "NatCash"
  },
  {
    "key": "moncash",
    "label": "MonCash"
  },
  {
    "key": "account",
    "label": "Compte & emplacement"
  },
  {
    "key": "admin",
    "label": "Administration"
  }
]

export const HELP = {
  "home": {
    "group": "dashboard",
    "icon": "view-dashboard",
    "title": "Tableau de bord",
    "summary": "C'est la page d'accueil qui vous donne un resume rapide de l'activite de votre commerce pour la periode choisie : ventes, transactions, meilleurs produits et alertes de stock.",
    "steps": [
      {
        "title": "Consulter vos chiffres cles",
        "body": "En haut, les quatre cartes vous montrent le total des ventes, le nombre de transactions, la vente moyenne et les articles vendus, avec une comparaison par rapport a la periode precedente."
      },
      {
        "title": "Suivre les ventes dans le temps",
        "body": "Les graphiques affichent les ventes par heure pour la journee et l'evolution sur les 7 derniers jours, pour voir vos moments les plus actifs."
      },
      {
        "title": "Surveiller le stock",
        "body": "Le bloc Alertes de stock liste les produits dont la quantite est basse ou epuisee : pensez a reapprovisionner ceux qui apparaissent en rouge ou orange."
      },
      {
        "title": "Actualiser ou ouvrir une vente",
        "body": "Cliquez sur Actualiser pour recharger les donnees, ou sur l'icone en forme d'oeil dans Transactions recentes pour voir le detail d'une vente."
      }
    ],
    "faqs": [
      {
        "q": "Pourquoi mon tableau de bord est vide ?",
        "a": "Cela signifie simplement qu'il n'y a pas encore de vente enregistree pour la periode affichee. Des qu'une vente sera faite, les chiffres apparaitront automatiquement."
      },
      {
        "q": "Que veut dire la fleche verte ou rouge sur les cartes ?",
        "a": "Elle compare votre resultat a la periode precedente (par exemple a hier). Une fleche verte indique une hausse, une fleche rouge une baisse."
      },
      {
        "q": "Comment changer la periode affichee ?",
        "a": "La periode est celle selectionnee dans le filtre de dates en haut de l'application. Modifiez-la, puis revenez au tableau de bord pour voir les chiffres correspondants."
      }
    ]
  },
  "search": {
    "group": "dashboard",
    "icon": "receipt-text-search-outline",
    "title": "Rechercher une vente",
    "summary": "Cette page vous permet de retrouver rapidement une vente precise en tapant son numero de fiche.",
    "steps": [
      {
        "title": "Saisir le numero de fiche",
        "body": "Tapez le numero de la fiche de vente dans la barre de recherche. Les resultats s'affichent automatiquement au fur et a mesure de votre saisie."
      },
      {
        "title": "Lire les resultats",
        "body": "Chaque ligne montre l'emplacement, l'agent, le client, le montant, la date et le statut (Actif ou Annule) de la vente trouvee."
      },
      {
        "title": "Ouvrir la vente",
        "body": "Cliquez sur une ligne pour ouvrir la fiche complete de la vente et en voir tous les details."
      }
    ],
    "faqs": [
      {
        "q": "Je ne trouve aucun resultat, pourquoi ?",
        "a": "Verifiez que le numero de fiche est correct. La recherche se fait sur le numero exact ; un chiffre manquant ou en trop peut empecher de trouver la vente."
      },
      {
        "q": "Puis-je chercher par nom de client ?",
        "a": "Non, cette page recherche uniquement par numero de fiche. Pour filtrer par agent ou periode, utilisez plutot le Rapport de ventes."
      }
    ]
  },
  "sales": {
    "group": "reports",
    "icon": "cash-register",
    "title": "Rapport de ventes",
    "summary": "Ce rapport liste toutes vos ventes de la periode avec leurs totaux, et permet de les filtrer puis de les exporter en PDF ou Excel.",
    "steps": [
      {
        "title": "Filtrer les ventes",
        "body": "Choisissez un agent, un statut (Actif ou Annule) ou saisissez un numero de vente, puis cliquez sur le bouton de validation pour afficher les ventes correspondantes."
      },
      {
        "title": "Lire les totaux",
        "body": "En haut, les cartes Total HTG et Total USD additionnent uniquement les ventes actives de la periode (les ventes annulees ne sont pas comptees)."
      },
      {
        "title": "Voir le detail d'une vente",
        "body": "Dans le tableau, cliquez sur l'icone en forme d'oeil au bout d'une ligne pour ouvrir la fiche complete de cette vente."
      },
      {
        "title": "Exporter le rapport",
        "body": "Utilisez le bouton PDF ou Excel pour telecharger la liste et la partager ou l'archiver."
      }
    ],
    "faqs": [
      {
        "q": "Pourquoi le total ne correspond pas au nombre de lignes ?",
        "a": "Le total ne compte que les ventes actives. Les ventes annulees restent affichees dans la liste mais ne sont pas incluses dans les montants."
      },
      {
        "q": "Comment changer la periode du rapport ?",
        "a": "La periode est definie par le filtre de dates de l'application. Modifiez les dates de debut et de fin, puis le rapport se met a jour."
      }
    ]
  },
  "sales-summary": {
    "group": "reports",
    "icon": "chart-areaspline",
    "title": "Recapitulatif des ventes",
    "summary": "Cette page resume vos performances sur une semaine avec vos ventes brutes, remboursements, reductions, ventes nettes, cout des marchandises et marge, jour par jour.",
    "steps": [
      {
        "title": "Choisir la semaine",
        "body": "Utilisez les fleches gauche et droite pour passer d'une semaine a l'autre. La periode affichee est rappelee au centre."
      },
      {
        "title": "Filtrer par horaire",
        "body": "Cliquez sur le bouton d'heure pour analyser toute la journee ou seulement une plage horaire precise (par exemple vos heures d'ouverture)."
      },
      {
        "title": "Explorer un indicateur",
        "body": "Cliquez sur une carte (Ventes brutes, Remboursements, Reductions, Ventes nettes ou Marge) pour l'afficher dans le graphique d'evolution."
      },
      {
        "title": "Exporter le recapitulatif",
        "body": "Telechargez le resume en PDF ou Excel grace aux boutons prevus a cet effet."
      }
    ],
    "faqs": [
      {
        "q": "Quelle difference entre ventes brutes et ventes nettes ?",
        "a": "Les ventes brutes sont le total avant ajustements. Les ventes nettes retirent les remboursements et les reductions : c'est ce que vous avez reellement encaisse."
      },
      {
        "q": "Que signifie le pourcentage en couleur sur chaque carte ?",
        "a": "Il compare le resultat de la semaine affichee a la semaine precedente. En vert il indique une hausse, en rouge une baisse."
      }
    ]
  },
  "returns": {
    "group": "reports",
    "icon": "backup-restore",
    "title": "Rapport de retours",
    "summary": "Ce rapport regroupe tous les retours et remboursements de la periode, avec leurs montants et produits, et permet de les exporter.",
    "steps": [
      {
        "title": "Filtrer les retours",
        "body": "Selectionnez un agent, un statut ou un numero de retour, puis validez pour afficher les retours qui correspondent."
      },
      {
        "title": "Lire le total des retours",
        "body": "La ligne TOTAL en haut du tableau additionne les montants rembourses et le nombre de produits retournes pour la periode."
      },
      {
        "title": "Voir le detail d'un retour",
        "body": "Cliquez sur l'icone en forme d'oeil au bout d'une ligne pour ouvrir la fiche du retour concerne."
      },
      {
        "title": "Exporter le rapport",
        "body": "Telechargez la liste des retours en PDF ou Excel pour vos archives."
      }
    ],
    "faqs": [
      {
        "q": "Quelle difference avec le rapport de ventes ?",
        "a": "Le rapport de ventes montre vos ventes ; ce rapport montre uniquement les retours et remboursements, c'est-a-dire l'argent rendu aux clients."
      },
      {
        "q": "Aucun retour n'apparait, est-ce normal ?",
        "a": "Oui, si aucun retour n'a ete enregistre sur la periode choisie. C'est plutot une bonne nouvelle pour votre commerce."
      }
    ]
  },
  "productsreport": {
    "group": "reports",
    "icon": "package-variant",
    "title": "Rapport par produit",
    "summary": "Ce rapport montre, produit par produit, les quantites vendues, le cout, le chiffre d'affaires, le profit et la marge sur la periode.",
    "steps": [
      {
        "title": "Filtrer les produits",
        "body": "Choisissez un emplacement et/ou une categorie, puis validez pour n'afficher que les produits concernes."
      },
      {
        "title": "Reperer vos meilleurs produits",
        "body": "Les produits sont classes du plus vendu au moins vendu. La fleche a cote de la quantite indique si les ventes montent ou baissent par rapport a la periode precedente."
      },
      {
        "title": "Analyser la rentabilite",
        "body": "Comparez le cout, les ventes et le profit de chaque produit. La pastille de marge est verte si elle est bonne, orange si elle est moyenne et rouge si elle est faible."
      },
      {
        "title": "Exporter le rapport",
        "body": "Telechargez le rapport en PDF ou Excel pour l'analyser ou le partager."
      }
    ],
    "faqs": [
      {
        "q": "Comment est calcule le profit ?",
        "a": "Le profit correspond aux ventes du produit moins son cout d'achat. La marge est ce profit exprime en pourcentage des ventes."
      },
      {
        "q": "Que signifie la fleche verte ou rouge a cote de la quantite ?",
        "a": "Elle compare les quantites vendues a la periode precedente : verte si le produit se vend davantage, rouge s'il se vend moins."
      }
    ]
  },
  "agents": {
    "group": "reports",
    "icon": "account-tie",
    "title": "Rapport par agent",
    "summary": "Ce rapport presente les ventes realisees par chaque agent ainsi que la commission qui lui revient sur la periode.",
    "steps": [
      {
        "title": "Filtrer par emplacement ou appareil",
        "body": "Selectionnez un emplacement et/ou un appareil de caisse, puis validez pour affiner les resultats par agent."
      },
      {
        "title": "Lire les ventes de chaque agent",
        "body": "Le tableau montre, pour chaque agent, ses ventes en HTG et en USD, ainsi que le total converti dans la monnaie choisie."
      },
      {
        "title": "Verifier les commissions",
        "body": "La colonne Commission affiche le montant du a chaque agent, calcule a partir de son taux et de ses ventes."
      },
      {
        "title": "Exporter le rapport",
        "body": "Telechargez le rapport en PDF ou Excel, par exemple pour preparer la paie ou les primes."
      }
    ],
    "faqs": [
      {
        "q": "Comment est calculee la commission d'un agent ?",
        "a": "Elle correspond au total des ventes de l'agent (converti dans la monnaie choisie) multiplie par son taux de commission en pourcentage."
      },
      {
        "q": "Pourquoi un agent n'apparait pas dans la liste ?",
        "a": "Un agent n'apparait que s'il a realise des ventes sur la periode et selon les filtres choisis. Sans vente enregistree, il ne figure pas au rapport."
      }
    ]
  },
  "closing": {
    "group": "reports",
    "icon": "cash-register",
    "title": "Rapport de caisse",
    "summary": "Cette page resume vos encaissements de la periode par mode de paiement (cash, carte, cheque, credit, NatCash, MonCash) en gourdes et en dollars, avec les annulations, les retours et le net.",
    "steps": [
      {
        "title": "Choisir la periode",
        "body": "Le rapport utilise la periode (du... au...) selectionnee en haut de l'application. Changez ces dates pour voir une autre journee ou une autre periode."
      },
      {
        "title": "Filtrer par agent ou appareil",
        "body": "Selectionnez un agent et/ou un appareil (caisse) dans les listes, puis cliquez sur le bouton de validation (coche) pour afficher uniquement leurs encaissements."
      },
      {
        "title": "Lire les totaux",
        "body": "Les cartes du haut montrent les ventes HTG et USD ainsi que les annulations et retours. Le tableau detaille chaque mode de paiement et affiche le Total des ventes puis le NET (ventes moins annulations et retours)."
      },
      {
        "title": "Exporter le rapport",
        "body": "Cliquez sur le bouton PDF pour un document imprimable, ou sur le bouton Excel pour recuperer les chiffres dans un tableur."
      }
    ],
    "faqs": [
      {
        "q": "Que signifie le montant NET en bas du tableau ?",
        "a": "Le NET correspond au total de vos ventes, duquel on retire les annulations et les retours. C'est ce qui reste reellement encaisse pour la periode."
      },
      {
        "q": "Pourquoi je vois deux colonnes, HTG et USD ?",
        "a": "Vos encaissements sont separes par devise : la colonne HTG montre ce qui a ete recu en gourdes et la colonne USD ce qui a ete recu en dollars, sans melanger les deux."
      },
      {
        "q": "Comment voir la caisse d'un seul vendeur ?",
        "a": "Choisissez son nom dans la liste Agent puis validez avec la coche. Pour revenir a la vue complete, utilisez le bouton de reinitialisation (fleche circulaire)."
      }
    ]
  },
  "storesreport": {
    "group": "reports",
    "icon": "store",
    "title": "Ventes par emplacement",
    "summary": "Cette page affiche le total des ventes de chaque emplacement sur la periode choisie, dans la devise selectionnee (gourdes ou dollars).",
    "steps": [
      {
        "title": "Choisir la periode",
        "body": "Le rapport se base sur la periode (du... au...) definie en haut de l'application. Modifiez ces dates pour comparer une autre periode."
      },
      {
        "title": "Lire le tableau",
        "body": "Chaque ligne represente un emplacement avec son total de ventes. La ligne TOTAL en haut additionne les ventes de tous vos emplacements."
      },
      {
        "title": "Comprendre la conversion",
        "body": "Les ventes faites dans une autre devise sont automatiquement converties dans la devise affichee, selon le taux enregistre au moment de chaque vente."
      }
    ],
    "faqs": [
      {
        "q": "Pourquoi le total est affiche dans une seule devise ?",
        "a": "Pour pouvoir comparer les emplacements facilement, toutes les ventes sont ramenees a une meme devise (gourdes ou dollars) en utilisant le taux du jour de chaque vente."
      },
      {
        "q": "Comment changer la devise du rapport ?",
        "a": "La devise depend du reglage de devise choisi dans l'application. Modifiez ce reglage pour voir les totaux en gourdes ou en dollars."
      }
    ]
  },
  "sale-details": {
    "group": "reports",
    "icon": "receipt-text",
    "title": "Detail d'une vente",
    "summary": "Cette page montre tous les details d'une vente (ou d'un retour) : produits, quantites, prix, paiements, client, agent et emplacement, avec la possibilite d'annuler la vente.",
    "steps": [
      {
        "title": "Consulter la vente",
        "body": "Le haut de page indique la date, le client, l'agent et l'emplacement. Le tableau Produits liste chaque article avec sa quantite, son prix unitaire et son total."
      },
      {
        "title": "Verifier les paiements",
        "body": "La section Paiements montre comment le client a regle (cash, carte, etc.), le montant par devise et la monnaie eventuellement rendue."
      },
      {
        "title": "Annuler une vente",
        "body": "Si la vente est encore active, cliquez sur Annuler, indiquez la raison de l'annulation, puis confirmez. L'annulation est tracee avec votre nom et la date."
      },
      {
        "title": "Telecharger la fiche",
        "body": "Cliquez sur le bouton PDF pour obtenir une fiche de vente (ou de retour) imprimable a remettre au client ou a archiver."
      }
    ],
    "faqs": [
      {
        "q": "Pourquoi certaines ventes sont marquees RETOUR ?",
        "a": "Une vente dont le total est negatif correspond a un retour de marchandise. Elle est identifiee par une etiquette RETOUR en haut de la fiche."
      },
      {
        "q": "Puis-je annuler une vente deja annulee ?",
        "a": "Non. Une vente deja annulee affiche un bandeau rouge avec la date, l'auteur et la raison ; le bouton Annuler n'est alors plus disponible."
      },
      {
        "q": "L'annulation supprime-t-elle la vente ?",
        "a": "Non, la vente reste visible mais marquee comme annulee, avec la raison enregistree. Rien n'est efface, tout reste tracable."
      }
    ]
  },
  "categories": {
    "group": "catalog",
    "icon": "shape",
    "title": "Categories",
    "summary": "Cette page sert a creer et organiser les categories qui regroupent vos produits (par exemple Boissons, Plats, Accessoires).",
    "steps": [
      {
        "title": "Creer une categorie",
        "body": "Cliquez sur Nouvelle categorie, saisissez un nom, choisissez le statut (Actif ou Inactif), puis cliquez sur Creer."
      },
      {
        "title": "Modifier une categorie",
        "body": "Cliquez sur l'icone crayon a droite de la ligne pour changer son nom ou son statut, puis enregistrez."
      },
      {
        "title": "Rechercher une categorie",
        "body": "Utilisez le champ Rechercher en haut a droite pour retrouver rapidement une categorie dans la liste."
      }
    ],
    "faqs": [
      {
        "q": "A quoi sert la colonne Produits ?",
        "a": "Elle indique combien de produits sont actuellement ranges dans cette categorie, pour voir d'un coup d'oeil son contenu."
      },
      {
        "q": "Que se passe-t-il si je mets une categorie en Inactif ?",
        "a": "La categorie reste enregistree mais n'est plus proposee pour le classement courant. Vous pouvez la reactiver a tout moment en la modifiant."
      }
    ]
  },
  "products": {
    "group": "catalog",
    "icon": "package-variant",
    "title": "Produits",
    "summary": "Cette page sert a gerer vos produits : nom, code-barres, categorie, cout, prix de vente, image et composition en ingredients.",
    "steps": [
      {
        "title": "Ajouter un produit",
        "body": "Cliquez sur Nouveau produit, remplissez le nom, la categorie, le cout et le prix de vente, puis cliquez sur Creer. Le code-barres et le prix minimum sont optionnels."
      },
      {
        "title": "Ajouter une image",
        "body": "Cliquez sur la vignette d'image a gauche de la ligne pour choisir une photo du produit depuis votre appareil."
      },
      {
        "title": "Definir la composition",
        "body": "Pour un produit prepare, cliquez sur l'icone plat pour lui associer des ingredients avec leurs quantites et unites, afin de suivre la consommation de stock."
      }
    ],
    "faqs": [
      {
        "q": "Ou voir et ajuster le stock de mes produits ?",
        "a": "Le suivi du stock se fait dans le Rapport de Stock (menu Inventaire), qui montre l'etat de chaque produit sur la periode. Pour enregistrer des receptions ou des ajustements, utilisez Nouveau Lot."
      },
      {
        "q": "Dois-je saisir les prix en gourdes ou en dollars ?",
        "a": "Les montants sont toujours enregistres en gourdes. Si vous avez un emplacement en dollars, vous pouvez saisir le montant en USD et le champ en gourdes se remplit automatiquement selon le taux du jour."
      },
      {
        "q": "Ou est affiche le code-barres d'un produit ?",
        "a": "Passez la souris sur le nom du produit dans la liste : une petite bulle affiche son code-barres."
      }
    ]
  },
  "catalog": {
    "group": "catalog",
    "icon": "table-edit",
    "title": "Catalogue (edition rapide)",
    "summary": "Cette page presente vos produits sous forme de tableau type tableur pour modifier rapidement plusieurs noms, categories, codes-barres et prix en une seule fois.",
    "steps": [
      {
        "title": "Modifier directement les cases",
        "body": "Cliquez dans une case et tapez la nouvelle valeur. Les lignes modifiees s'affichent en surbrillance et un compteur indique le nombre de produits changes."
      },
      {
        "title": "Se deplacer au clavier",
        "body": "Utilisez Tab, Entree et les fleches du clavier pour passer d'une case a l'autre, comme dans un tableur, sans souris."
      },
      {
        "title": "Rechercher un produit",
        "body": "Utilisez le champ Rechercher en haut a droite (par nom ou code-barres) pour filtrer le tableau et trouver vite la ligne a corriger."
      },
      {
        "title": "Sauvegarder",
        "body": "Quand vos modifications sont pretes, cliquez sur Sauvegarder pour enregistrer toutes les lignes modifiees d'un coup."
      }
    ],
    "faqs": [
      {
        "q": "Quelle difference avec la page Produits ?",
        "a": "La page Produits sert a gerer un produit en detail (image, stock, composition). Le Catalogue sert a corriger rapidement beaucoup de produits a la fois sur les champs essentiels."
      },
      {
        "q": "Mes modifications sont-elles enregistrees automatiquement ?",
        "a": "Non. Tant que vous n'avez pas clique sur Sauvegarder, les changements ne sont pas enregistres. Le compteur de produits modifies vous le rappelle."
      }
    ]
  },
  "import": {
    "group": "catalog",
    "icon": "file-upload",
    "title": "Import de donnees",
    "summary": "Cette page permet d'importer en une fois vos categories, produits et clients a partir d'un fichier Excel, avec une etape de verification avant l'enregistrement.",
    "steps": [
      {
        "title": "Telecharger le modele",
        "body": "Recuperez le fichier modele propose, qui contient les trois onglets attendus : Categories, Produits et Clients."
      },
      {
        "title": "Remplir le fichier",
        "body": "Completez chaque onglet sans changer le nom des colonnes ni l'ordre des onglets, puis enregistrez votre fichier au format .xlsx."
      },
      {
        "title": "Deposer le fichier",
        "body": "Glissez le fichier dans la zone prevue ou cliquez pour le selectionner. Les donnees s'affichent ensuite par onglets pour verification."
      },
      {
        "title": "Verifier puis enregistrer",
        "body": "Double-cliquez sur une case pour la corriger, ajoutez ou supprimez des lignes si besoin, puis cliquez sur Enregistrer pour importer definitivement."
      }
    ],
    "faqs": [
      {
        "q": "Quel format de fichier est accepte ?",
        "a": "Un fichier Excel .xlsx de 5 Mo maximum, contenant exactement les trois onglets Categories, Produits et Clients."
      },
      {
        "q": "Puis-je corriger les donnees avant l'import ?",
        "a": "Oui. Apres le depot du fichier, vous pouvez verifier et modifier chaque donnee dans le tableau ; rien n'est enregistre tant que vous n'avez pas clique sur Enregistrer."
      },
      {
        "q": "Pourquoi mon fichier est-il refuse ?",
        "a": "Le plus souvent parce que le nom des colonnes ou des onglets a ete change, ou parce qu'une colonne obligatoire est vide. Repartez du modele sans en modifier la structure."
      }
    ]
  },
  "loyverse-import": {
    "group": "catalog",
    "icon": "import",
    "title": "Migration Loyverse",
    "summary": "Recopier automatiquement votre catalogue (categories, produits, stock) et vos clients depuis votre compte Loyverse vers votre caisse, sans rien modifier dans Loyverse.",
    "steps": [
      {
        "title": "Connecter votre compte Loyverse",
        "body": "Dans Loyverse, allez dans Integrations puis Jetons d'acces, creez un jeton et copiez-le. Revenez ici, collez-le dans le champ et cliquez sur Verifier et enregistrer. Le nom de votre commerce s'affiche si tout est bon."
      },
      {
        "title": "Analyser avant d'importer",
        "body": "Cliquez sur Analyser pour voir combien de categories, produits et clients seront repris. Cette etape ne modifie rien, elle vous donne juste un apercu."
      },
      {
        "title": "Lancer l'import",
        "body": "Cliquez sur Importer et laissez la page ouverte. A la fin, un tableau indique pour chaque type le nombre d'elements crees, mis a jour et en erreur."
      },
      {
        "title": "Reprendre l'historique des ventes",
        "body": "Plus bas, choisissez la periode puis cliquez sur Analyser les ventes et Importer les ventes. Importez toujours le catalogue avant les ventes."
      }
    ],
    "faqs": [
      {
        "q": "Vais-je creer des doublons si je relance l'import ?",
        "a": "Non. Vous pouvez relancer autant de fois que vous voulez : les elements deja presents sont mis a jour, pas dupliques. Les produits sont reconnus par leur code-barres et les clients par leur telephone."
      },
      {
        "q": "Pourquoi mes prix ne sont pas en dollars ?",
        "a": "Tout est importe en gourdes (HTG). Si certains articles etaient en dollars dans Loyverse, pensez a ajuster ces prix apres l'import."
      },
      {
        "q": "Que faire si la colonne Erreurs n'est pas a zero ?",
        "a": "Le reste a bien ete importe. Telechargez le journal d'erreurs : il liste les elements non repris et la raison, pour que vous puissiez les corriger."
      }
    ]
  },
  "rates": {
    "group": "catalog",
    "icon": "currency-usd",
    "title": "Taux du Jour",
    "summary": "Definir le taux de conversion du dollar (USD) en gourdes (HTG) utilise pour les ventes de chaque emplacement.",
    "steps": [
      {
        "title": "Reperer votre emplacement",
        "body": "Chaque carte affichee correspond a un emplacement, avec son nom en haut."
      },
      {
        "title": "Saisir le taux",
        "body": "Dans la case orange, indiquez combien de gourdes valent 1 dollar aujourd'hui (par exemple 132.50)."
      },
      {
        "title": "Enregistrer",
        "body": "Cliquez ailleurs sur la page ou quittez la case : le taux est sauvegarde automatiquement et la mention Enregistre s'affiche."
      }
    ],
    "faqs": [
      {
        "q": "A quoi sert ce taux ?",
        "a": "Il sert a convertir les montants en dollars vers les gourdes lors des ventes. Mettez-le a jour chaque jour ou des qu'il change."
      },
      {
        "q": "Faut-il cliquer sur un bouton pour sauvegarder ?",
        "a": "Non, il n'y a pas de bouton. Modifiez le montant puis cliquez en dehors de la case : la sauvegarde se fait toute seule."
      },
      {
        "q": "Le message Aucun taux configure s'affiche, que faire ?",
        "a": "Cela veut dire qu'aucun taux n'est encore prepare pour vos emplacements. Contactez le support pour qu'il l'active."
      }
    ]
  },
  "operations": {
    "group": "catalog",
    "icon": "swap-horizontal",
    "title": "Rapport d'Operations",
    "summary": "Consulter et corriger toutes les operations de stock (ventes, receptions, ajustements) enregistrees sur la periode choisie.",
    "steps": [
      {
        "title": "Choisir la periode",
        "body": "Les operations affichees correspondent a la periode selectionnee en haut du tableau de bord. Changez la periode pour voir d'autres dates."
      },
      {
        "title": "Rechercher une operation",
        "body": "Utilisez la barre Rechercher un Produit pour retrouver rapidement les operations liees a un article precis."
      },
      {
        "title": "Modifier une operation",
        "body": "Cliquez sur l'icone crayon d'une ligne pour corriger le type, la quantite, le cout ou le memo, puis cliquez sur Valider."
      },
      {
        "title": "Supprimer une operation",
        "body": "Cliquez sur l'icone corbeille pour retirer une operation erronee. Le stock est recalcule automatiquement."
      }
    ],
    "faqs": [
      {
        "q": "Que signifient les types Vente, Reception, Ajustement ?",
        "a": "Vente fait baisser le stock, Reception l'augmente (nouvel arrivage), Ajustement corrige une difference (inventaire, perte) et Autre couvre les cas restants."
      },
      {
        "q": "Si je corrige une operation, le stock se met-il a jour ?",
        "a": "Oui. Apres chaque modification ou suppression, le stock des produits concernes est recalcule automatiquement."
      },
      {
        "q": "Je ne vois aucune operation, est-ce normal ?",
        "a": "Verifiez la periode selectionnee : si aucune operation n'a eu lieu sur ces dates, le tableau reste vide. Elargissez la periode si besoin."
      }
    ]
  },
  "new-batche": {
    "group": "inventory",
    "icon": "package-variant-plus",
    "title": "Nouveau Lot",
    "summary": "Enregistrer en une seule fois l'entree ou l'ajustement de stock de plusieurs produits, comme dans un tableur.",
    "steps": [
      {
        "title": "Ajouter des produits",
        "body": "Cherchez un produit dans la barre du haut pour l'ajouter un par un, ou utilisez Par categorie ou Tous les produits pour en ajouter plusieurs d'un coup."
      },
      {
        "title": "Saisir quantites et couts",
        "body": "Pour chaque ligne, indiquez la quantite et le cout unitaire. La colonne Stock projete vous montre le stock futur, et le Total se calcule tout seul."
      },
      {
        "title": "Enregistrer le lot",
        "body": "Cliquez sur Enregistrer, choisissez le type (Reception ou Ajustement), ajoutez un memo expliquant le lot, puis validez."
      },
      {
        "title": "Exporter en Excel (optionnel)",
        "body": "Avant d'enregistrer, cliquez sur Excel pour telecharger une copie du lot et la verifier ou la partager."
      }
    ],
    "faqs": [
      {
        "q": "Quelle difference entre Reception et Ajustement ?",
        "a": "Reception sert a enregistrer un nouvel arrivage de marchandise. Ajustement sert a corriger le stock apres un inventaire, une perte ou une erreur."
      },
      {
        "q": "Pourquoi un stock projete s'affiche en rouge ?",
        "a": "Le rouge signale que le stock deviendrait negatif avec cette quantite. Verifiez la quantite saisie avant d'enregistrer."
      },
      {
        "q": "Puis-je tout recommencer sans enregistrer ?",
        "a": "Oui. Cliquez sur Reinitialiser pour vider le lot et repartir de zero sans rien sauvegarder."
      }
    ]
  },
  "batches": {
    "group": "inventory",
    "icon": "format-list-bulleted",
    "title": "Liste des Lots",
    "summary": "Voir l'historique de tous les lots d'entree et d'ajustement de stock enregistres sur la periode choisie.",
    "steps": [
      {
        "title": "Parcourir les lots",
        "body": "Le tableau liste chaque lot avec son numero, son memo, son type, le nombre de produits et la date. La liste depend de la periode choisie en haut du tableau de bord."
      },
      {
        "title": "Rechercher un lot",
        "body": "Utilisez la barre Rechercher pour retrouver un lot par son numero ou par son memo."
      },
      {
        "title": "Ouvrir un lot",
        "body": "Cliquez sur une ligne pour voir le detail complet du lot et tous ses produits."
      },
      {
        "title": "Creer ou exporter",
        "body": "Cliquez sur Nouveau lot pour en creer un, ou sur PDF / Excel pour telecharger la liste complete."
      }
    ],
    "faqs": [
      {
        "q": "Pourquoi un lot recent n'apparait pas ?",
        "a": "La liste se limite a la periode selectionnee. Si le lot est en dehors de ces dates, elargissez la periode en haut du tableau de bord."
      },
      {
        "q": "A quoi correspondent les couleurs de la colonne Type ?",
        "a": "Elles distinguent rapidement les Receptions (arrivages) des Ajustements (corrections de stock)."
      },
      {
        "q": "Comment voir ce qu'il y a dans un lot ?",
        "a": "Cliquez sur la ligne du lot : vous accedez a sa fiche detaillee avec la liste des produits, les quantites et les couts."
      }
    ]
  },
  "batch-details": {
    "group": "inventory",
    "icon": "package-variant-closed",
    "title": "Details du Lot",
    "summary": "Consulter la fiche complete d'un lot : son type, sa date, son memo et tous les produits avec quantites, couts et totaux.",
    "steps": [
      {
        "title": "Lire les informations cles",
        "body": "En haut, retrouvez le numero du lot, son type, sa date et le nombre de produits. Le memo rappelle la raison du lot."
      },
      {
        "title": "Examiner les produits",
        "body": "Le tableau detaille chaque produit avec sa quantite, son cout unitaire et son total. Les totaux generaux sont affiches en bas."
      },
      {
        "title": "Telecharger en PDF",
        "body": "Cliquez sur PDF pour obtenir une version imprimable du lot, a archiver ou a partager."
      }
    ],
    "faqs": [
      {
        "q": "Puis-je modifier le lot depuis cette page ?",
        "a": "Non, cette fiche est en lecture seule. Pour corriger une operation, passez par le Rapport d'Operations."
      },
      {
        "q": "Que represente le Total cout en bas ?",
        "a": "C'est la valeur totale du lot, c'est-a-dire la somme des quantites multipliees par leurs couts unitaires."
      },
      {
        "q": "A quoi sert le bouton PDF ?",
        "a": "Il genere un document propre et imprimable du lot, avec le nom de votre commerce, pratique pour vos archives ou un fournisseur."
      }
    ]
  },
  "stock": {
    "group": "inventory",
    "icon": "warehouse",
    "title": "Rapport de Stock",
    "summary": "Visualiser l'etat du stock de chaque produit sur la periode choisie, avec sa valeur au cout, le chiffre d'affaires previsionnel et les stocks negatifs a surveiller. Tous les produits sont charges, meme au-dela de 2000.",
    "steps": [
      {
        "title": "Suivre le chargement",
        "body": "La page charge vos produits par vagues de 200. Le sous-titre affiche la progression (par exemple 600 / 2300 produits) jusqu'a ce que tout soit charge. Vous pouvez commencer a lire le tableau pendant le chargement."
      },
      {
        "title": "Lire les indicateurs cles",
        "body": "Les cinq cartes du haut resument le Stock Total, la Valeur au Cout, le CA Previsionnel, le nombre de produits en Stock Negatif et le nombre de produits Avec Ventes sur la periode."
      },
      {
        "title": "Filtrer par categorie ou par nom",
        "body": "Utilisez la liste Toutes les categories pour ne charger qu'une categorie (pratique pour faire l'inventaire rayon par rayon), ou tapez un nom dans Rechercher un produit : la recherche interroge directement le serveur et couvre tous vos produits."
      },
      {
        "title": "Isoler les stocks a surveiller ou les produits vendus",
        "body": "Utilisez les pastilles Stock negatif, Stock faible (10 ou moins) et Avec ventes dans la barre de filtres, ou cliquez directement sur les cartes Stock Negatif et Avec Ventes. Le tableau, les totaux et les exports suivent ce qui est affiche. Stock negatif et Stock faible ne se combinent pas entre eux."
      },
      {
        "title": "Analyser le tableau",
        "body": "Chaque ligne montre, pour un produit, le stock initial, les ventes, les receptions, les ajustements et le stock final. Les stocks negatifs apparaissent en rouge."
      },
      {
        "title": "Exporter le rapport",
        "body": "Cliquez sur PDF ou Excel pour telecharger les lignes affichees (filtres compris) et les conserver ou les partager."
      }
    ],
    "faqs": [
      {
        "q": "Que veut dire un stock final en rouge ?",
        "a": "Le rouge indique un stock negatif : l'ordinateur compte plus de sorties que d'entrees. Cela signale souvent des ventes non enregistrees ou un arrivage oublie a corriger."
      },
      {
        "q": "Que signifie le compteur N / total du sous-titre ?",
        "a": "Le premier nombre est ce qui est affiche, le second le nombre total de produits correspondant a vos filtres. A la fin du chargement, les deux doivent correspondre : c'est votre garantie que l'inventaire est complet."
      },
      {
        "q": "Que faire si l'alerte Ecart detecte apparait ?",
        "a": "Elle signale que le nombre de produits charges ne correspond pas au total attendu (souvent une coupure reseau). Cliquez sur Recharger pour relancer le chargement complet."
      },
      {
        "q": "Quelle difference entre Valeur au Cout et CA Previsionnel ?",
        "a": "La Valeur au Cout est ce que votre stock vous a coute. Le CA Previsionnel est ce qu'il rapporterait s'il etait entierement vendu au prix de vente."
      },
      {
        "q": "Le rapport tient-il compte de la periode ?",
        "a": "Oui. Les colonnes ventes, receptions et ajustements correspondent a la periode choisie en haut du tableau de bord. Changez la periode pour une autre analyse."
      }
    ]
  },
  "stock-movements": {
    "group": "inventory",
    "icon": "swap-vertical",
    "title": "Mouvements de stock",
    "summary": "Cette page retrace tout l'historique des entrees et sorties de stock d'un produit, du stock de depart au stock final.",
    "steps": [
      {
        "title": "Choisir un produit",
        "body": "Dans la barre de recherche, tapez le nom du produit (ou son code-barres) et selectionnez-le dans la liste."
      },
      {
        "title": "Charger l'historique",
        "body": "Cliquez sur le bouton Charger. Le tableau affiche chaque reception, chaque vente et chaque ajustement avec la date, la quantite et le solde apres chaque operation."
      },
      {
        "title": "Lire le resume",
        "body": "En haut, les quatre encadres vous donnent d'un coup d'oeil le stock initial, le total des receptions, le total des ventes et le stock final."
      },
      {
        "title": "Telecharger un rapport",
        "body": "Utilisez les boutons PDF ou Excel en haut a droite pour enregistrer l'historique du produit et le conserver ou l'imprimer."
      }
    ],
    "faqs": [
      {
        "q": "Pourquoi le tableau est-il vide ?",
        "a": "Soit aucun produit n'est selectionne, soit ce produit n'a eu aucun mouvement sur la periode choisie. Verifiez le produit et la periode active en haut de l'application."
      },
      {
        "q": "Que veut dire le solde negatif en rouge ?",
        "a": "Un solde en rouge signifie que plus d'unites sont sorties que d'unites disponibles. Pensez a enregistrer une reception pour regulariser votre stock."
      }
    ]
  },
  "stock-alerts": {
    "group": "inventory",
    "icon": "alert-circle-outline",
    "title": "Alertes et valeur du stock",
    "summary": "Cette page vous montre la valeur totale de votre stock et signale les produits en stock bas, en rupture ou en negatif.",
    "steps": [
      {
        "title": "Consulter le resume",
        "body": "Les encadres du haut affichent le nombre de produits, la valeur totale de votre stock (au cout d'achat), le nombre de produits en stock bas et ceux en negatif."
      },
      {
        "title": "Filtrer les produits",
        "body": "Cliquez sur les onglets Tous, Negatif, Stock Bas ou Rupture pour n'afficher que les produits a surveiller."
      },
      {
        "title": "Examiner la liste",
        "body": "Pour chaque produit, le tableau indique le stock, le seuil d'alerte, le cout, le prix de vente, la valeur du stock et un statut colore (OK, Bas, Rupture, Negatif)."
      },
      {
        "title": "Exporter au besoin",
        "body": "Cliquez sur PDF ou Excel pour enregistrer la liste, par exemple pour preparer une commande de reapprovisionnement."
      }
    ],
    "faqs": [
      {
        "q": "Comment definir le seuil d'alerte d'un produit ?",
        "a": "Le seuil d'alerte se regle dans la fiche du produit, dans la section Produits. Des que le stock passe sous ce seuil, le produit apparait en Stock Bas ici."
      },
      {
        "q": "Que signifie la valeur totale du stock ?",
        "a": "C'est la somme de la valeur de tous vos produits, calculee au cout d'achat (stock multiplie par le cout unitaire). Cela represente l'argent immobilise dans votre marchandise."
      }
    ]
  },
  "customers": {
    "group": "customers",
    "icon": "account-group",
    "title": "Clients",
    "summary": "Cette page regroupe tous vos clients avec leur solde, leur credit disponible et permet de les creer ou les modifier.",
    "steps": [
      {
        "title": "Ajouter un client",
        "body": "Cliquez sur Nouveau client, renseignez le nom et le telephone, et si besoin une limite de credit et une reduction, puis enregistrez."
      },
      {
        "title": "Rechercher et filtrer",
        "body": "Utilisez la barre de recherche pour retrouver un client par nom ou telephone, ou l'onglet Avec solde pour voir uniquement ceux qui vous doivent de l'argent."
      },
      {
        "title": "Modifier une fiche",
        "body": "Cliquez sur l'icone crayon pour mettre a jour les informations, la limite de credit, la reduction ou le statut d'un client."
      },
      {
        "title": "Voir le releve de compte",
        "body": "Cliquez sur l'icone document pour ouvrir le releve de compte du client et suivre ses achats et paiements."
      }
    ],
    "faqs": [
      {
        "q": "A quoi sert la limite de credit ?",
        "a": "Elle fixe le montant maximum qu'un client peut devoir. Le credit disponible indique combien il peut encore acheter a credit avant d'atteindre cette limite."
      },
      {
        "q": "Pourquoi un solde apparait-il en rouge ?",
        "a": "Un solde en rouge signifie que le client vous doit encore de l'argent. Un solde vert (ou zero) indique qu'il est a jour."
      }
    ]
  },
  "customer-statements": {
    "group": "customers",
    "icon": "file-document-multiple-outline",
    "title": "Releves du client",
    "summary": "Cette page liste les releves mensuels d'un client avec ses ventes, paiements et solde pour chaque periode.",
    "steps": [
      {
        "title": "Verifier la situation du client",
        "body": "Les encadres du haut rappellent le solde, le credit disponible, la limite de credit et la reduction du client."
      },
      {
        "title": "Ouvrir un releve mensuel",
        "body": "Cliquez sur une ligne du tableau (ou sur l'icone oeil) pour voir le detail des operations d'un mois donne."
      },
      {
        "title": "Suivre le statut",
        "body": "La colonne Statut indique si le releve du mois est Paye, Du, Partiel ou En cours."
      },
      {
        "title": "Telecharger un releve",
        "body": "Cliquez sur l'icone PDF rouge a droite d'une ligne pour enregistrer ce releve mensuel et l'envoyer ou l'imprimer pour le client."
      }
    ],
    "faqs": [
      {
        "q": "Comment enregistrer un paiement du client ?",
        "a": "Ouvrez le releve du mois concerne en cliquant dessus, puis utilisez le bouton Enregistrer Paiement sur la page de detail."
      },
      {
        "q": "Aucun releve ne s'affiche, est-ce normal ?",
        "a": "Oui si le client n'a encore eu aucune vente. Les releves mensuels sont crees automatiquement des qu'il y a des operations a son compte."
      }
    ]
  },
  "statement-detail": {
    "group": "customers",
    "icon": "file-document-outline",
    "title": "Detail du releve",
    "summary": "Cette page montre toutes les operations d'un mois pour un client (ventes et paiements) et permet d'enregistrer un nouveau paiement.",
    "steps": [
      {
        "title": "Lire l'historique des operations",
        "body": "Le tableau affiche chaque vente et chaque paiement avec la date, le montant en gourdes ou en dollars, et le solde cumule apres chaque ligne."
      },
      {
        "title": "Enregistrer un paiement",
        "body": "Cliquez sur Enregistrer Paiement, saisissez le montant, choisissez la devise (HTG ou USD), indiquez le taux du jour, puis validez."
      },
      {
        "title": "Basculer entre HTG et USD",
        "body": "Utilisez l'interrupteur Totaux du releve pour afficher le recapitulatif en gourdes ou en dollars selon le besoin."
      },
      {
        "title": "Exporter en PDF",
        "body": "Cliquez sur PDF, choisissez le format des montants (gourdes seules, dollars seuls ou les deux), puis telechargez le releve."
      }
    ],
    "faqs": [
      {
        "q": "Pourquoi le taux de change est-il obligatoire ?",
        "a": "Le taux du jour (combien de gourdes pour 1 dollar) permet de convertir correctement les montants entre les deux monnaies, que le paiement soit en gourdes ou en dollars."
      },
      {
        "q": "Pourquoi un solde en USD affiche-t-il un tiret ?",
        "a": "Quand une vente en gourdes n'a pas de taux de change, le total en dollars ne peut plus etre calcule a partir de cette ligne. Ajoutez un taux pour retablir le calcul."
      },
      {
        "q": "Puis-je saisir un montant negatif ?",
        "a": "Oui, un montant negatif est accepte pour corriger ou regulariser un solde, par exemple une dette anterieure."
      }
    ]
  },
  "statement-settings": {
    "group": "customers",
    "icon": "cog-outline",
    "title": "Parametres des releves",
    "summary": "Cette page permet de personnaliser le message qui s'affiche en bas des releves de compte et des recus de vente.",
    "steps": [
      {
        "title": "Rediger le message du releve",
        "body": "Dans le premier champ, ecrivez le texte qui apparaitra en bas de chaque releve client : remerciement, modes de paiement acceptes, coordonnees, etc."
      },
      {
        "title": "Rediger le message du recu",
        "body": "Dans le second champ, ecrivez le message qui s'affichera sur les recus de vente, par exemple un mot de remerciement."
      },
      {
        "title": "Verifier l'apercu",
        "body": "L'apercu a droite vous montre en temps reel comment votre message apparaitra sur le document."
      },
      {
        "title": "Enregistrer",
        "body": "Cliquez sur Enregistrer pour appliquer vos messages a tous les futurs releves et recus."
      }
    ],
    "faqs": [
      {
        "q": "Ou apparaissent ces messages ?",
        "a": "Le message du releve s'affiche en bas des releves de compte client en PDF, et le message du recu s'affiche sur les recus de vente generes apres une vente."
      },
      {
        "q": "Dois-je remplir les deux messages ?",
        "a": "Non, les deux sont optionnels. Si un champ est laisse vide, aucun message ne s'affichera a cet endroit sur le document."
      }
    ]
  },
  "restaurant-tables": {
    "group": "restaurant",
    "icon": "table-furniture",
    "title": "Tables",
    "summary": "Cette page sert a gerer les tables de votre restaurant, leur zone, leur capacite et leur disponibilite.",
    "steps": [
      {
        "title": "Ajouter une table",
        "body": "Cliquez sur Nouvelle table, indiquez son nom, sa zone (terrasse, salle, bar), sa capacite, puis enregistrez."
      },
      {
        "title": "Filtrer et rechercher",
        "body": "Utilisez les onglets Actives, Inactives, Occupees, Disponibles ou la barre de recherche pour retrouver rapidement une table."
      },
      {
        "title": "Voir une table occupee",
        "body": "Pour une table marquee Occupee, cliquez sur l'icone oeil pour consulter la commande en cours a cette table."
      },
      {
        "title": "Modifier ou supprimer",
        "body": "Utilisez l'icone crayon pour modifier une table, ou l'icone corbeille pour la supprimer (action definitive)."
      }
    ],
    "faqs": [
      {
        "q": "Quelle difference entre Active et Disponible ?",
        "a": "Active veut dire que la table est utilisable dans le service. Disponible veut dire qu'aucun client n'y est installe en ce moment, tandis qu'Occupee signifie qu'une commande y est en cours."
      },
      {
        "q": "A quoi sert la zone ?",
        "a": "La zone (terrasse, salle, bar) aide a organiser et retrouver vos tables par section du restaurant."
      }
    ]
  },
  "restaurant-orders": {
    "group": "restaurant",
    "icon": "receipt-text-outline",
    "title": "Commandes",
    "summary": "Cette page liste toutes les commandes du restaurant et vous permet de les filtrer, les consulter et les exporter.",
    "steps": [
      {
        "title": "Choisir une période",
        "body": "Renseignez les dates Du et Au pour afficher uniquement les commandes de cette période, puis cliquez sur Filtrer."
      },
      {
        "title": "Affiner par serveur ou statut",
        "body": "Utilisez les listes Serveur et Statut (Ouvertes ou Fermées) pour ne voir que les commandes qui vous intéressent."
      },
      {
        "title": "Voir le détail d'une commande",
        "body": "Cliquez sur l'icône en forme d'oeil au bout d'une ligne pour ouvrir la fiche complète de la commande."
      },
      {
        "title": "Exporter la liste",
        "body": "Cliquez sur le bouton vert pour télécharger un fichier Excel, ou sur le bouton rouge pour imprimer ou enregistrer un PDF."
      }
    ],
    "faqs": [
      {
        "q": "Pourquoi ne vois-je aucune commande ?",
        "a": "Vérifiez les dates choisies en haut de page. Par défaut, seules les commandes des 7 derniers jours s'affichent. Élargissez la période puis cliquez sur Filtrer."
      },
      {
        "q": "Que veut dire le total affiché en bas du tableau ?",
        "a": "C'est la somme de toutes les commandes actuellement affichées. Il change selon les filtres que vous appliquez."
      }
    ]
  },
  "order-detail": {
    "group": "restaurant",
    "icon": "receipt-text-check-outline",
    "title": "Détail de la commande",
    "summary": "Cette page affiche tous les détails d'une commande : le client, les articles, les totaux, l'historique et permet de l'annuler.",
    "steps": [
      {
        "title": "Consulter les informations",
        "body": "Retrouvez en haut la date d'ouverture, le serveur, la table et le nombre de couverts, ainsi que les coordonnées du client."
      },
      {
        "title": "Vérifier les articles et le total",
        "body": "Les articles sont regroupés par catégorie avec quantité et prix. Le bas du tableau affiche le sous-total, la taxe, le service et le total."
      },
      {
        "title": "Suivre l'historique",
        "body": "À droite, l'historique montre chaque action effectuée sur la commande (ouverture, ajout d'articles, fermeture) avec l'heure et la personne concernée."
      },
      {
        "title": "Annuler ou exporter",
        "body": "Si la commande est encore ouverte, le bouton Annuler la commande la clôture en libérant la table (une raison est demandée). Le bouton Exporter PDF imprime la fiche."
      }
    ],
    "faqs": [
      {
        "q": "Puis-je annuler une commande déjà fermée ?",
        "a": "Non. Le bouton d'annulation n'apparaît que pour les commandes encore ouvertes. Une fois fermée, la commande ne peut plus être annulée ici."
      },
      {
        "q": "Pourquoi dois-je indiquer une raison pour annuler ?",
        "a": "L'annulation est irréversible et libère la table. La raison est conservée dans l'historique pour garder une trace claire."
      }
    ]
  },
  "restaurant-servers": {
    "group": "restaurant",
    "icon": "account-tie-outline",
    "title": "Serveurs",
    "summary": "Cette page vous permet de gérer la liste de vos serveurs : les ajouter, les modifier ou les désactiver.",
    "steps": [
      {
        "title": "Ajouter un serveur",
        "body": "Cliquez sur Nouveau serveur, saisissez son nom et son téléphone, laissez le statut sur Actif, puis cliquez sur Créer."
      },
      {
        "title": "Rechercher un serveur",
        "body": "Utilisez la barre de recherche pour retrouver rapidement un serveur par son nom ou son téléphone."
      },
      {
        "title": "Modifier ou désactiver",
        "body": "Cliquez sur l'icône crayon pour mettre à jour les informations. Basculez le statut sur Inactif pour qu'un serveur ne soit plus proposé sans le supprimer."
      },
      {
        "title": "Supprimer un serveur",
        "body": "Cliquez sur l'icône corbeille puis confirmez. Attention, cette suppression est définitive."
      }
    ],
    "faqs": [
      {
        "q": "Faut-il supprimer un serveur qui ne travaille plus ?",
        "a": "Mieux vaut le passer en Inactif. Ainsi il n'apparaît plus dans les nouvelles commandes mais reste lié aux commandes passées et aux rapports."
      },
      {
        "q": "À quoi sert la liste des serveurs ?",
        "a": "Les serveurs créés ici peuvent être assignés aux commandes et apparaissent dans les filtres et le rapport de commissions."
      }
    ]
  },
  "restaurant-commissions": {
    "group": "restaurant",
    "icon": "cash-multiple",
    "title": "Commissions par serveur",
    "summary": "Cette page calcule, pour chaque serveur, ses ventes et la commission basée sur les frais de service des commandes fermées.",
    "steps": [
      {
        "title": "Choisir le périmètre",
        "body": "Sélectionnez un emplacement et/ou un serveur dans les listes en haut, puis cliquez sur le bouton de validation pour afficher le rapport."
      },
      {
        "title": "Lire le tableau",
        "body": "Chaque ligne montre le nombre de commandes, les ventes en HTG et USD, les frais de service et la commission. La ligne du haut affiche les totaux."
      },
      {
        "title": "Réinitialiser les filtres",
        "body": "Cliquez sur le bouton de rafraîchissement pour effacer les filtres et revoir tous les serveurs de la période."
      },
      {
        "title": "Exporter le rapport",
        "body": "Utilisez le bouton PDF pour une version imprimable ou le bouton Excel pour un fichier que vous pouvez retravailler."
      }
    ],
    "faqs": [
      {
        "q": "Comment est calculée la commission ?",
        "a": "Elle repose sur les frais de service des commandes fermées du serveur, convertis dans la devise affichée (HTG ou USD) selon le taux en vigueur."
      },
      {
        "q": "Pourquoi certaines commandes ne comptent pas ?",
        "a": "Seules les commandes fermées sont prises en compte. Les commandes ouvertes ou annulées n'entrent pas dans le calcul des commissions."
      }
    ]
  },
  "supervisor-codes": {
    "group": "restaurant",
    "icon": "shield-key-outline",
    "title": "Codes superviseur",
    "summary": "Cette page gère les codes secrets exigés pour autoriser la modification ou la suppression d'articles sur une commande depuis l'application.",
    "steps": [
      {
        "title": "Créer un code",
        "body": "Cliquez sur Nouveau code, donnez-lui un label (ex : Manager), saisissez le code secret, laissez-le Actif puis cliquez sur Créer."
      },
      {
        "title": "Afficher ou masquer un code",
        "body": "Cliquez sur l'icône en forme d'oeil à côté d'un code pour le révéler quand vous avez besoin de le communiquer."
      },
      {
        "title": "Modifier ou désactiver",
        "body": "Cliquez sur le crayon pour changer le code ou son label. Passez-le en Inactif pour le suspendre temporairement sans le supprimer."
      },
      {
        "title": "Supprimer un code",
        "body": "Cliquez sur la corbeille puis confirmez. Cette action est définitive."
      }
    ],
    "faqs": [
      {
        "q": "À quoi servent ces codes ?",
        "a": "Ils protègent les commandes : sur l'application, un code valide est demandé avant de modifier ou retirer un article d'une commande de table."
      },
      {
        "q": "Que faire si un code a été divulgué ?",
        "a": "Modifiez-le immédiatement via le crayon, ou désactivez-le, puis créez-en un nouveau à communiquer aux personnes autorisées."
      }
    ]
  },
  "ingredients": {
    "group": "ingredients",
    "icon": "food-variant",
    "title": "Ingrédients",
    "summary": "Cette page recense vos ingrédients avec leur unité, leur coût, leur stock et un seuil d'alerte pour éviter les ruptures.",
    "steps": [
      {
        "title": "Ajouter un ingrédient",
        "body": "Cliquez sur Nouveau, saisissez le nom, choisissez l'unité (kg, l, unité...), indiquez le coût, le stock initial et le seuil d'alerte, puis Enregistrer."
      },
      {
        "title": "Repérer les stocks bas",
        "body": "Une valeur de stock affichée en rouge signale que l'ingrédient est au niveau d'alerte ou en dessous : il faut le réapprovisionner."
      },
      {
        "title": "Rechercher et modifier",
        "body": "Utilisez la barre de recherche pour trouver un ingrédient, puis cliquez sur le crayon pour ajuster son coût, son unité ou son seuil d'alerte."
      },
      {
        "title": "Activer ou désactiver",
        "body": "Utilisez l'interrupteur Actif dans la fiche pour retirer temporairement un ingrédient sans le supprimer."
      }
    ],
    "faqs": [
      {
        "q": "Pourquoi je ne peux pas modifier le stock d'un ingrédient existant ?",
        "a": "Le stock initial se fixe à la création. Ensuite, il évolue par les mouvements de stock, pas en modifiant directement le champ ici."
      },
      {
        "q": "À quoi sert le seuil d'alerte ?",
        "a": "Dès que le stock atteint ce seuil ou descend en dessous, la valeur passe en rouge pour vous prévenir qu'il est temps de réapprovisionner."
      }
    ]
  },
  "compositions": {
    "group": "ingredients",
    "icon": "puzzle-outline",
    "title": "Compositions",
    "summary": "Cette page relie vos produits à leurs ingrédients en indiquant la quantité utilisée, ce qui sert au suivi des coûts et du stock.",
    "steps": [
      {
        "title": "Choisir un produit",
        "body": "Dans la liste de gauche, recherchez et cliquez sur le produit dont vous voulez définir la recette. Le nombre d'ingrédients liés s'affiche à côté de chaque produit."
      },
      {
        "title": "Ajouter des ingrédients",
        "body": "Cliquez sur Ingrédient pour ajouter une ligne, sélectionnez l'ingrédient, saisissez la quantité et choisissez l'unité."
      },
      {
        "title": "Retirer une ligne",
        "body": "Cliquez sur l'icône corbeille en bout de ligne pour enlever un ingrédient de la composition."
      },
      {
        "title": "Enregistrer la composition",
        "body": "Une fois toutes les lignes complétées (ingrédient, quantité et unité), cliquez sur Enregistrer pour sauvegarder la recette du produit."
      }
    ],
    "faqs": [
      {
        "q": "Pourquoi le bouton Enregistrer reste-t-il grisé ?",
        "a": "Chaque ligne doit avoir un ingrédient choisi, une quantité supérieure à zéro et une unité. Complétez toutes les lignes pour activer l'enregistrement."
      },
      {
        "q": "À quoi sert de composer un produit ?",
        "a": "Cela décrit ce qui entre dans chaque produit, ce qui permet de suivre les coûts de revient et l'impact des ventes sur le stock d'ingrédients."
      }
    ]
  },
  "ingredient-stock": {
    "group": "ingredients",
    "icon": "warehouse",
    "title": "Stock des ingrédients",
    "summary": "Cette page montre l'état du stock de chaque ingrédient sur la période choisie, avec le stock final et sa valeur en argent.",
    "steps": [
      {
        "title": "Choisir la période",
        "body": "Sélectionnez vos dates de début et de fin (filtre de période en haut du tableau de bord). Le rapport se met à jour automatiquement avec les mouvements de cette période."
      },
      {
        "title": "Lire le tableau",
        "body": "Pour chaque ingrédient, vous voyez le stock initial, les réceptions, les ajustements, les sorties, le stock final et la valeur en argent. La ligne TOTAL en haut donne les sommes."
      },
      {
        "title": "Rechercher un ingrédient",
        "body": "Tapez le nom d'un ingrédient dans la case Rechercher pour n'afficher que les lignes qui vous intéressent."
      },
      {
        "title": "Exporter le rapport",
        "body": "Cliquez sur PDF pour imprimer ou partager, ou sur Excel pour retravailler les chiffres dans un tableur."
      }
    ],
    "faqs": [
      {
        "q": "Pourquoi un stock final apparaît en rouge ?",
        "a": "Un chiffre en rouge signifie un stock final négatif : il y a eu plus de sorties que de quantités reçues. Vérifiez vos réceptions et vos mouvements pour cet ingrédient."
      },
      {
        "q": "Que veut dire la colonne Valeur Coût ?",
        "a": "C'est la valeur en argent de votre stock restant, calculée en multipliant la quantité finale par le coût de l'ingrédient. Le total en haut vous donne la valeur de tout votre stock."
      },
      {
        "q": "Les chiffres ne couvrent pas la bonne période, que faire ?",
        "a": "Vérifiez les dates de début et de fin choisies en haut du tableau de bord, puis revenez sur cette page."
      }
    ]
  },
  "ingredient-movements": {
    "group": "ingredients",
    "icon": "swap-vertical",
    "title": "Mouvements d'ingrédients",
    "summary": "Cette page enregistre et liste toutes les entrées, sorties et ajustements de stock pour vos ingrédients.",
    "steps": [
      {
        "title": "Consulter l'historique",
        "body": "Le tableau liste chaque mouvement avec la date, l'ingrédient, le type (Réception, Sortie, Ajustement), la quantité, le coût et un mémo."
      },
      {
        "title": "Ajouter un mouvement",
        "body": "Cliquez sur Nouveau, choisissez l'ingrédient, l'emplacement et le type de mouvement, saisissez la quantité (et le coût total si besoin), puis Enregistrer."
      },
      {
        "title": "Rechercher",
        "body": "Utilisez la case Rechercher pour retrouver rapidement les mouvements d'un ingrédient précis."
      }
    ],
    "faqs": [
      {
        "q": "Quelle est la différence entre les types de mouvement ?",
        "a": "Réception ajoute du stock (livraison reçue), Sortie retire du stock, et Ajustement corrige une quantité (inventaire, perte, casse). Choisissez celui qui correspond à votre situation."
      },
      {
        "q": "Faut-il toujours mettre un coût ?",
        "a": "Le coût total est facultatif, mais le renseigner lors des réceptions permet de garder une valeur de stock juste dans vos rapports."
      },
      {
        "q": "Pourquoi une quantité s'affiche en rouge ?",
        "a": "Une quantité en rouge indique une sortie de stock, c'est-à-dire une diminution de vos quantités disponibles."
      }
    ]
  },
  "ingredient-alerts": {
    "group": "ingredients",
    "icon": "alert-circle-outline",
    "title": "Alertes de stock",
    "summary": "Cette page affiche les ingrédients dont le stock est passé sous le seuil d'alerte et qu'il faut bientôt réapprovisionner.",
    "steps": [
      {
        "title": "Repérer les ingrédients à risque",
        "body": "Le tableau ne montre que les ingrédients en alerte, avec leur stock actuel, le seuil défini et l'écart entre les deux."
      },
      {
        "title": "Planifier le réapprovisionnement",
        "body": "Notez les ingrédients listés et prévoyez une commande auprès de vos fournisseurs avant la rupture."
      },
      {
        "title": "Enregistrer la réception",
        "body": "Une fois la livraison reçue, allez dans Mouvements d'ingrédients pour saisir une Réception. L'ingrédient disparaîtra de la liste une fois le stock remonté au-dessus du seuil."
      }
    ],
    "faqs": [
      {
        "q": "Pourquoi un ingrédient apparaît dans les alertes ?",
        "a": "Son stock actuel est descendu au niveau ou en dessous du seuil d'alerte que vous avez défini pour cet ingrédient."
      },
      {
        "q": "Le message Aucune alerte s'affiche, est-ce normal ?",
        "a": "Oui, cela veut dire que tous vos ingrédients ont un stock suffisant, au-dessus de leur seuil. C'est une bonne nouvelle."
      },
      {
        "q": "Comment modifier le seuil d'alerte ?",
        "a": "Le seuil se règle sur la fiche de chaque ingrédient, dans la gestion des ingrédients."
      }
    ]
  },
  "ingredients-report": {
    "group": "ingredients",
    "icon": "food-variant",
    "title": "Consommation d'ingrédients",
    "summary": "Cette page indique quelle quantité de chaque ingrédient a été consommée sur la période et combien cela a coûté.",
    "steps": [
      {
        "title": "Choisir la période",
        "body": "Sélectionnez vos dates de début et de fin en haut du tableau de bord pour cibler la période à analyser."
      },
      {
        "title": "Lire les indicateurs",
        "body": "Les cartes du haut résument le nombre d'ingrédients consommés et le coût total. Le tableau détaille pour chaque ingrédient la quantité consommée, le coût unitaire et le coût total."
      },
      {
        "title": "Rechercher et exporter",
        "body": "Filtrez par nom avec la case Rechercher, puis exportez le rapport en PDF ou en Excel si besoin."
      }
    ],
    "faqs": [
      {
        "q": "Que signifie la quantité consommée ?",
        "a": "C'est la quantité totale de l'ingrédient utilisée durant la période, par exemple à travers vos ventes et préparations."
      },
      {
        "q": "À quoi sert ce rapport ?",
        "a": "Il vous aide à comprendre vos coûts de matières premières, à repérer les ingrédients les plus coûteux et à mieux gérer vos achats."
      }
    ]
  },
  "natcash/payments": {
    "group": "natcash",
    "icon": "cellphone-check",
    "title": "Transactions Natcash",
    "summary": "Cette page liste tous les paiements reçus par Natcash avec leur montant, leur payeur et leur statut.",
    "steps": [
      {
        "title": "Choisir la période",
        "body": "Définissez vos dates de début et de fin en haut du tableau de bord pour afficher les transactions de la période voulue."
      },
      {
        "title": "Filtrer et rechercher",
        "body": "Utilisez la case Rechercher (numéro, payeur, identifiant, montant) et le menu Statut pour ne voir que les paiements Confirmés, En attente ou Annulés."
      },
      {
        "title": "Lire le total",
        "body": "La ligne TOTAL en haut du tableau additionne le montant de toutes les transactions affichées."
      },
      {
        "title": "Exporter",
        "body": "Cliquez sur l'icône PDF ou Excel pour télécharger la liste des transactions."
      }
    ],
    "faqs": [
      {
        "q": "Que veulent dire les statuts des paiements ?",
        "a": "Confirmé : le paiement a bien été reçu. En attente : il n'est pas encore finalisé. Annulé ou Refusé : le paiement n'a pas abouti."
      },
      {
        "q": "Le tableau est vide, pourquoi ?",
        "a": "Il n'y a aucune transaction Natcash pour la période choisie ou pour le filtre appliqué. Élargissez la période ou cliquez sur le bouton de réinitialisation."
      }
    ]
  },
  "natcash/withdrawal-requests": {
    "group": "natcash",
    "icon": "cash-fast",
    "title": "Retraits Natcash",
    "summary": "Cette page suit vos demandes de retrait d'argent Natcash avec le montant, les frais, le net à recevoir et leur statut.",
    "steps": [
      {
        "title": "Consulter les demandes",
        "body": "Le tableau affiche pour chaque demande le téléphone, le montant, les frais, le montant à recevoir, la date et les statuts."
      },
      {
        "title": "Filtrer la liste",
        "body": "Recherchez par téléphone, montant ou statut, ou choisissez un statut (Approuvé, Payé, En attente, Rejeté) dans le menu, puis validez."
      },
      {
        "title": "Voir le détail",
        "body": "Cliquez sur l'icône en forme d'œil au bout d'une ligne pour ouvrir le détail complet de la demande de retrait."
      },
      {
        "title": "Exporter",
        "body": "Téléchargez la liste avec les boutons PDF ou Excel pour la conserver ou la partager."
      }
    ],
    "faqs": [
      {
        "q": "Quelle différence entre Statut et Paiement ?",
        "a": "Le Statut indique où en est votre demande de retrait, et le Paiement indique l'état côté partenaire (Natcash). Les deux doivent être à Payé pour que l'argent soit bien sorti."
      },
      {
        "q": "Pourquoi y a-t-il des frais sur le montant ?",
        "a": "Les frais sont prélevés sur le retrait. La colonne À recevoir indique le montant net qui vous revient une fois les frais déduits."
      }
    ]
  },
  "moncash/payments": {
    "group": "moncash",
    "icon": "cellphone-check",
    "title": "Transactions MonCash",
    "summary": "Cette page liste tous les paiements reçus par MonCash avec leur montant, leur payeur et leur statut.",
    "steps": [
      {
        "title": "Choisir la période",
        "body": "Définissez vos dates de début et de fin en haut du tableau de bord pour afficher les transactions de la période voulue."
      },
      {
        "title": "Filtrer et rechercher",
        "body": "Utilisez la case Rechercher (numéro, payeur, identifiant, montant) et le menu Statut pour ne voir que les paiements Confirmés, En attente ou Annulés."
      },
      {
        "title": "Lire le total",
        "body": "La ligne TOTAL en haut du tableau additionne le montant de toutes les transactions affichées."
      },
      {
        "title": "Exporter",
        "body": "Cliquez sur l'icône PDF ou Excel pour télécharger la liste des transactions."
      }
    ],
    "faqs": [
      {
        "q": "Que veulent dire les statuts des paiements ?",
        "a": "Confirmé : le paiement a bien été reçu. En attente : il n'est pas encore finalisé. Annulé ou Refusé : le paiement n'a pas abouti."
      },
      {
        "q": "Le tableau est vide, pourquoi ?",
        "a": "Il n'y a aucune transaction MonCash pour la période choisie ou pour le filtre appliqué. Élargissez la période ou cliquez sur le bouton de réinitialisation."
      }
    ]
  },
  "withdrawal-requests": {
    "group": "moncash",
    "icon": "cash-fast",
    "title": "Retraits MonCash",
    "summary": "Cette page liste toutes les demandes de retrait MonCash avec leur montant, les frais, le statut et l'argent net a recevoir.",
    "steps": [
      {
        "title": "Trouver une demande",
        "body": "Tapez un numero de telephone, un montant ou un statut dans la barre de recherche pour retrouver rapidement une demande precise."
      },
      {
        "title": "Filtrer par statut",
        "body": "Choisissez un statut (En attente, Approuve, Paye ou Rejete) dans le menu deroulant pour n'afficher que les demandes concernees, puis utilisez le bouton de reinitialisation pour tout reafficher."
      },
      {
        "title": "Voir le detail",
        "body": "Cliquez sur l'icone en forme d'oeil au bout d'une ligne pour ouvrir la fiche complete de la demande de retrait."
      },
      {
        "title": "Exporter le rapport",
        "body": "Utilisez le bouton PDF ou le bouton Excel pour telecharger la liste des retraits, avec les totaux, afin de la conserver ou de l'imprimer."
      }
    ],
    "faqs": [
      {
        "q": "Quelle est la difference entre Montant, Frais et A recevoir ?",
        "a": "Le Montant est la somme totale demandee, les Frais sont preleves par le service, et A recevoir est l'argent net que le client touche reellement apres les frais."
      },
      {
        "q": "Que veulent dire les couleurs des statuts ?",
        "a": "Le vert signifie que le retrait est approuve ou paye, le bleu qu'il est en attente de traitement, et le rouge qu'il a ete rejete."
      },
      {
        "q": "A quoi sert la colonne Paiement a cote du Statut ?",
        "a": "Elle indique l'etat du paiement chez le partenaire MonCash, c'est-a-dire si l'argent a bien ete verse de leur cote."
      }
    ]
  },
  "account": {
    "group": "account",
    "icon": "office-building",
    "title": "Profil de l'entreprise",
    "summary": "Cette page regroupe les informations de votre entreprise (coordonnees, contact, logo et message du recu) que vous pouvez consulter et mettre a jour.",
    "steps": [
      {
        "title": "Consulter vos informations",
        "body": "Affichez en un coup d'oeil le nom, le telephone, l'e-mail, l'adresse, la personne de contact et le message imprime sur vos recus."
      },
      {
        "title": "Modifier les informations",
        "body": "Cliquez sur le bouton Modifier en haut a droite, corrigez les champs voulus dans la fenetre, puis cliquez sur Enregistrer."
      },
      {
        "title": "Ajouter ou changer le logo",
        "body": "Cliquez sur Ajouter logo ou Changer, choisissez une image depuis votre appareil, verifiez l'apercu puis cliquez sur Televerser."
      },
      {
        "title": "Supprimer le logo",
        "body": "Si un logo est present, cliquez sur l'icone de corbeille a cote du bouton du logo et confirmez pour le retirer."
      }
    ],
    "faqs": [
      {
        "q": "Pourquoi ne puis-je pas modifier le nom de l'entreprise ?",
        "a": "Le nom de l'entreprise est verrouille pour garantir la coherence de vos documents. Contactez le support si vous devez le faire changer."
      },
      {
        "q": "A quoi sert le message du recu ?",
        "a": "C'est le texte qui s'affiche en bas de vos recus de vente, par exemple un remerciement ou vos horaires d'ouverture."
      },
      {
        "q": "Le logo apparaitra-t-il sur mes documents ?",
        "a": "Oui, une fois televerse, votre logo represente votre entreprise dans l'application et sur vos documents."
      }
    ]
  },
  "stores": {
    "group": "account",
    "icon": "store",
    "title": "Emplacements",
    "summary": "Cette page liste vos points de vente et permet de regler leurs parametres : coordonnees, taxe, devise, prix minimum, commissions et options restaurant.",
    "steps": [
      {
        "title": "Consulter vos emplacements",
        "body": "Parcourez le tableau pour voir chaque emplacement avec son telephone, son e-mail, son adresse, sa taxe et sa devise."
      },
      {
        "title": "Modifier un emplacement",
        "body": "Cliquez sur l'icone crayon au bout d'une ligne pour ouvrir la fiche de l'emplacement et changer ses informations."
      },
      {
        "title": "Regler taxe et devise",
        "body": "Dans la fiche, indiquez le pourcentage de taxe et choisissez la devise de l'emplacement (HTG ou USD), puis enregistrez."
      },
      {
        "title": "Activer les options restaurant",
        "body": "Activez au besoin la gestion des tables, l'impression cuisine, l'impression bar ou les produits composes, puis cliquez sur Enregistrer."
      }
    ],
    "faqs": [
      {
        "q": "Que signifient les options Commission et Prix minimum ?",
        "a": "Commission active le calcul des commissions des agents pour cet emplacement, et Prix minimum empeche de vendre en dessous d'un prix plancher. Le statut Actif ou Inactif indique si l'option est en marche."
      },
      {
        "q": "A quoi servent les options restaurant ?",
        "a": "Elles adaptent l'application au mode restaurant : suivi des tables, envoi des commandes vers les imprimantes de la cuisine ou du bar, et gestion de plats composes de plusieurs ingredients."
      },
      {
        "q": "Puis-je creer un nouvel emplacement ici ?",
        "a": "Cette page sert a consulter et modifier les emplacements existants. Pour ajouter un nouvel emplacement, rapprochez-vous du support."
      }
    ]
  },
  "devices": {
    "group": "account",
    "icon": "tablet-cellphone",
    "title": "Appareils",
    "summary": "Cette page liste les appareils (caisses, tablettes) lies a vos emplacements et permet de gerer leurs abonnements et leurs renouvellements par MonCash ou NatCash.",
    "steps": [
      {
        "title": "Ajouter un appareil",
        "body": "Cliquez sur le bouton plus, saisissez le nom, choisissez l'emplacement et entrez le numero de serie (au moins 10 caracteres), puis validez."
      },
      {
        "title": "Voir les details et l'abonnement",
        "body": "Cliquez sur l'icone en forme d'oeil pour ouvrir la fiche de l'appareil et consulter ses abonnements actifs, a venir et echoues ainsi que la prochaine echeance."
      },
      {
        "title": "Renouveler l'abonnement",
        "body": "Dans la fiche, choisissez un plan et une methode de paiement (MonCash par telephone, MonCash QR ou NatCash), puis cliquez sur Proceder au paiement."
      },
      {
        "title": "Verifier le paiement",
        "body": "Suivez les instructions a l'ecran (code PIN sur le telephone ou scan du QR code), puis cliquez sur Verifier le paiement pour confirmer que la transaction est bien passee."
      }
    ],
    "faqs": [
      {
        "q": "Pourquoi le numero de serie doit-il faire au moins 10 caracteres ?",
        "a": "Le numero de serie identifie l'appareil de facon unique. Une longueur minimale evite les erreurs de saisie et garantit qu'il s'agit du bon equipement."
      },
      {
        "q": "Comment payer un renouvellement par MonCash sur le telephone ?",
        "a": "Choisissez la methode MonCash (telephone), entrez un numero de 8 chiffres, lancez le paiement, puis validez le code PIN recu sur ce telephone et cliquez sur Verifier le paiement."
      },
      {
        "q": "Que faire si un paiement reste en attente ?",
        "a": "Cliquez sur le bouton de verification a cote de l'abonnement concerne pour redemander le statut au fournisseur ; s'il reste bloque, attendez quelques minutes puis reverifiez."
      }
    ]
  },
  "configurations": {
    "group": "account",
    "icon": "cog-outline",
    "title": "Configurations",
    "summary": "Cette page permet de definir la taxe et les frais de service appliques automatiquement a vos commandes et ventes.",
    "steps": [
      {
        "title": "Definir la taxe",
        "body": "Saisissez le pourcentage de taxe a appliquer sur les commandes et ventes ; il est enregistre automatiquement des que vous quittez le champ."
      },
      {
        "title": "Definir les frais de service",
        "body": "Indiquez le pourcentage de frais de service pour les commandes restaurant ; la sauvegarde se fait toute seule."
      },
      {
        "title": "Verifier l'etat",
        "body": "Sous chaque champ, une etiquette indique Active avec le pourcentage en cours, ou Inactive si la valeur est a 0%."
      }
    ],
    "faqs": [
      {
        "q": "Dois-je cliquer sur un bouton pour sauvegarder ?",
        "a": "Non, les modifications sont enregistrees automatiquement. Un message de confirmation apparait apres chaque changement."
      },
      {
        "q": "Que se passe-t-il si je mets une valeur a 0% ?",
        "a": "Une valeur a 0% est consideree comme inactive et n'apparaitra pas sur les recus."
      },
      {
        "q": "A partir de quand ces reglages s'appliquent-ils ?",
        "a": "La taxe et les frais de service s'appliquent aux nouvelles commandes et ventes creees apres la modification, pas aux ventes deja enregistrees."
      }
    ]
  },
  "users": {
    "group": "admin",
    "icon": "account-group",
    "title": "Utilisateurs",
    "summary": "Cette page permet de gerer les comptes de votre personnel : creer, modifier, definir leur role, leur devise de vente et leur limite de rabais.",
    "steps": [
      {
        "title": "Creer un utilisateur",
        "body": "Cliquez sur Nouvel utilisateur, remplissez le nom, l'e-mail, le nom d'utilisateur et le code PIN, choisissez le role puis cliquez sur Valider."
      },
      {
        "title": "Rechercher un utilisateur",
        "body": "Utilisez la barre de recherche en haut du tableau pour retrouver une personne par son nom, son e-mail ou son nom d'utilisateur."
      },
      {
        "title": "Modifier un compte",
        "body": "Cliquez sur l'icone crayon au bout d'une ligne pour changer les informations, le role, le statut ou la limite de rabais de l'utilisateur."
      },
      {
        "title": "Definir les permissions de vente",
        "body": "Reglez la devise de vente (HTG, USD ou les deux), la commission et la limite de rabais que l'employe peut accorder, puis enregistrez."
      }
    ],
    "faqs": [
      {
        "q": "Comment changer le code PIN d'un employe ?",
        "a": "Ouvrez sa fiche avec le crayon et saisissez un nouveau PIN d'au moins 6 chiffres. Si vous laissez le champ vide, l'ancien PIN est conserve."
      },
      {
        "q": "A quoi sert la limite de rabais ?",
        "a": "Elle fixe le pourcentage de remise maximum qu'un employe peut accorder lors d'une vente, ce qui evite les rabais trop importants."
      },
      {
        "q": "Comment empecher un employe de se connecter ?",
        "a": "Modifiez sa fiche et passez son statut a Inactif ; il ne pourra plus se connecter sans etre supprime."
      }
    ]
  }
}

// Visite guidée d'accueil : surligne la navigation principale.
if (HELP.home) {
  HELP.home.tour = [
    { selector: '.app-sidebar', title: 'Le menu', body: 'Toute la navigation est ici : rapports, catalogue, inventaire, clients, etc.' },
    { selector: '.app-topbar', title: 'La barre du haut', body: 'Filtre de dates, emplacement et accès rapides se trouvent en haut.' },
    { selector: '[data-help-bubble]', title: 'Besoin d’aide ?', body: 'Cliquez sur ce bouton « ? » sur n’importe quelle page pour une aide contextuelle.' },
  ]
}

export function helpFor(name) {
  return (name && HELP[name]) || null
}
